import { useState } from "react";
import { Link } from "react-router-dom";
import { BellIcon } from "lucide-react";

import {
  useFeed,
  useMarkAllRead,
  useMarkRead,
  useUnreadCount,
  type FeedEntry,
} from "@/api/notifications";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatDate } from "@/lib/utils";


function severityBg(sev: string): string {
  if (sev === "critical") return "bg-red-100 text-red-800 border-red-200";
  if (sev === "warning") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-blue-100 text-blue-800 border-blue-200";
}


function FeedRow({ row, onClick }: { row: FeedEntry; onClick: () => void }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 border-b p-3 text-sm last:border-0",
        row.read ? "bg-background" : "bg-muted/40",
      )}
      onClick={onClick}
      role="button"
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "rounded border px-2 py-0.5 text-[10px] font-mono uppercase",
            severityBg(row.severity),
          )}
        >
          {row.severity}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {formatDate((row.created_at as any)?.local ?? row.created_at)}
        </span>
      </div>
      <p className="font-medium">{row.title ?? row.event_type}</p>
      {row.body && (
        <p className="text-xs text-muted-foreground line-clamp-2">{row.body}</p>
      )}
      {row.source_alert_id && (
        <Link to="/alerts" className="text-xs text-primary hover:underline">
          Go to alert →
        </Link>
      )}
    </div>
  );
}


export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: unread = 0 } = useUnreadCount();
  const { data: feed } = useFeed();
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className="relative rounded p-1 text-white hover:bg-white/10"
        aria-label="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 rounded-full bg-[#ff6d00] px-1.5 py-0.5 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <Card className="absolute right-0 top-full z-50 mt-2 w-96 max-h-[70vh] overflow-y-auto shadow-xl">
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b p-3">
              <p className="font-semibold">Notifications</p>
              <Button
                size="sm"
                variant="ghost"
                disabled={!unread}
                onClick={() => markAll.mutate()}
              >
                Mark all read
              </Button>
            </div>
            {(feed?.items ?? []).length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Nothing here yet.
              </p>
            )}
            {(feed?.items ?? []).map((row) => (
              <FeedRow
                key={row.id}
                row={row}
                onClick={() => !row.read && markRead.mutate(row.id)}
              />
            ))}
            <div className="border-t p-2 text-center">
              <Link
                to="/settings/notifications"
                className="text-xs text-primary hover:underline"
                onClick={() => setOpen(false)}
              >
                Manage rules + channels →
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
